import type { ButtonHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { Icon } from '../icon/Icon';
import { OddsFlashOverlay } from './OddsFlashOverlay';

/** Matches the Figma `State` property. */
export type OddsState = 'default' | 'selected' | 'suspended' | 'locked';
/** Matches the Figma `Flash` property: the direction the price last moved. */
export type OddsFlash = 'none' | 'up' | 'down';

const oddsButton = cva(
  'relative flex min-h-9 flex-1 items-center justify-between gap-2 overflow-hidden ' +
    'rounded-sm px-2.5 py-2 text-left transition-colors',
  {
    variants: {
      state: {
        default: 'bg-odds-default hover:bg-bg-elevated',
        selected: 'bg-odds-selected',
        suspended: 'bg-odds-suspended cursor-not-allowed',
        locked: 'bg-bg-elevated cursor-not-allowed',
      },
    },
    defaultVariants: { state: 'default' },
  },
);

export interface OddsButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'value'
> {
  /** Short outcome label, e.g. `1`, `X`, `Over 2.5`. */
  outcome?: string;
  /** Formatted price. Replaced by lock (locked) or em-dash (suspended). */
  odds: string;
  state?: OddsState;
  flash?: OddsFlash;
}

export function OddsButton({
  outcome,
  odds,
  state = 'default',
  flash = 'none',
  className,
  disabled,
  type = 'button',
  onClick,
  ...props
}: OddsButtonProps) {
  const unavailable = state === 'suspended' || state === 'locked';
  const selected = state === 'selected';
  const showFlash = flash !== 'none' && state !== 'suspended' && state !== 'locked';

  return (
    <button
      type={type}
      className={cn(oddsButton({ state }), className)}
      aria-pressed={selected}
      aria-disabled={unavailable || undefined}
      {...props}
      disabled={disabled === true || unavailable}
      onClick={unavailable ? undefined : onClick}
    >
      {/* Radial gradient overlay keeps the price legible while signalling direction. */}
      {showFlash && <OddsFlashOverlay flash={flash} />}

      {outcome !== undefined && (
        <span
          className={cn(
            'relative min-w-0 grow basis-0 truncate text-body-s',
            selected
              ? 'text-odds-selected-text'
              : unavailable
                ? 'text-text-disabled'
                : 'text-text-secondary',
          )}
        >
          {outcome}
        </span>
      )}
      <span className="relative flex shrink-0 items-center gap-1">
        {state === 'locked' ? (
          <Icon name="lock" size={14} label="Selection locked" className="text-icon-default" />
        ) : state === 'suspended' ? (
          <span
            className="text-odds tabular-nums text-text-disabled"
            aria-label="Selection suspended"
          >
            —
          </span>
        ) : (
          <>
            {/* An arrow carries the direction too, so the change is never colour-only. */}
            {showFlash && (
              <span
                className={cn(
                  'text-[9px] leading-none font-bold',
                  flash === 'up' ? 'text-odds-up' : 'text-odds-down',
                )}
              >
                <span aria-hidden>{flash === 'up' ? '▲' : '▼'}</span>
                <span className="sr-only">{flash === 'up' ? 'price up' : 'price down'}</span>
              </span>
            )}
            <span
              className={cn(
                'text-odds tabular-nums',
                selected ? 'text-odds-selected-text' : 'text-odds-default-text',
              )}
            >
              {odds}
            </span>
          </>
        )}
      </span>
    </button>
  );
}
